import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Search, RefreshCw,
  UserPlus, Building2, X, ShieldAlert, Smartphone, Check, Star,
  Stethoscope, ChevronDown, Copy, BellRing, Layers, CalendarDays,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { customersApi, ContactLookupHit } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import {
  isCallLogAvailable, checkCallLogPermission, requestCallLogPermission,
  getDeviceCallLog, normalizeNumber, displayNumber, probeCallLog, CALL_TYPE, DeviceCall,
  CallLogDiagnostics,
} from '../services/callLog';
import {
  getCallerIdStatus, requestPhonePermission, requestOverlayPermission,
  setCallerIdEnabled, syncDirectory, showTestPopup, requestScreeningRole,
  CallerIdStatus,
} from '../services/callerId';

type Filter = 'all' | 'missed' | 'incoming' | 'outgoing' | 'noanswer' | 'unknown';
type Range = 'today' | '7d' | '30d' | 'all';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'missed', label: 'Missed' },
  { key: 'incoming', label: 'Incoming' },
  { key: 'outgoing', label: 'Outgoing' },
  { key: 'noanswer', label: 'Not received' },
  { key: 'unknown', label: 'Unknown' },
];

// How many calls to READ from the device per range. A wider range must never
// return fewer rows than a narrower one — otherwise "All" shows less than
// "30 days", which is just wrong. Rendering cost is handled separately by
// RENDER_PAGE below, so these can be generous.
const RANGES: { key: Range; label: string; limit: number }[] = [
  { key: 'today', label: 'Today', limit: 1000 },
  { key: '7d', label: '7 days', limit: 3000 },
  { key: '30d', label: '30 days', limit: 6000 },
  { key: 'all', label: 'All', limit: 100000 },
];

// Rows drawn at once. Handsets hold thousands of calls and React will happily
// try to mount every one of them, so the list grows on demand instead.
const RENDER_PAGE = 200;

/** Lower bound for a range, in epoch millis. 0 means unbounded. */
function rangeSince(range: Range): number {
  if (range === 'all') return 0;
  if (range === 'today') {
    const d = new Date();
    d.setHours(0, 0, 0, 0); // calendar day, not a rolling 24 hours
    return d.getTime();
  }
  const days = range === '7d' ? 7 : 30;
  return Date.now() - days * 24 * 60 * 60 * 1000;
}

function callIcon(type: number) {
  switch (type) {
    case CALL_TYPE.INCOMING:
      return <PhoneIncoming className="h-4 w-4 text-emerald-600" />;
    case CALL_TYPE.OUTGOING:
      return <PhoneOutgoing className="h-4 w-4 text-blue-600" />;
    case CALL_TYPE.MISSED:
      return <PhoneMissed className="h-4 w-4 text-red-500" />;
    case CALL_TYPE.REJECTED:
    case CALL_TYPE.BLOCKED:
      return <PhoneMissed className="h-4 w-4 text-gray-400" />;
    default:
      return <Phone className="h-4 w-4 text-gray-400" />;
  }
}

function formatDuration(seconds: number) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatTime(ms: number) {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** The local calendar day a call belongs to, as YYYY-MM-DD. Local on purpose:
 *  a call at 11pm belongs to that evening, not to tomorrow in UTC. */
function dayKey(ms: number) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "Today" / "Yesterday" / "12 Sep 2026" — the heading a call is grouped under. */
function dayLabel(ms: number) {
  const d = new Date(ms);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (same(d, today)) return 'Today';
  if (same(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * True when a request never reached the server at all. fetch throws a bare
 * TypeError for that, which surfaces as the unhelpful "Failed to fetch" — so it
 * has to be told apart from a real server-side failure, otherwise a phone with
 * no signal reports a broken lookup instead of a missing connection.
 */
function isOffline(e: any): boolean {
  return e instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(e?.message || '');
}

/** Drag distance, in px, at which releasing places the call. */
const SWIPE_TRIGGER = 96;
const SWIPE_MAX = 140;

/**
 * Samsung-style swipe-to-call. Dragging a row to the right reveals a green call
 * panel; releasing past the trigger point dials the number.
 *
 * Direction is locked on the first few pixels of movement — without that, a
 * horizontal gesture and the page's vertical scroll fight each other and both
 * feel broken. `touch-action: pan-y` leaves scrolling to the browser.
 */
const SwipeToCall: React.FC<{
  number: string | null;
  className?: string;
  children: React.ReactNode;
}> = ({ number, className, children }) => {
  const [dx, setDx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const start = React.useRef<{ x: number; y: number } | null>(null);
  const axis = React.useRef<'h' | 'v' | null>(null);

  const armed = dx >= SWIPE_TRIGGER;

  const onTouchStart = (e: React.TouchEvent) => {
    if (!number) return;
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY };
    axis.current = null;
    setAnimating(false);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!start.current || !number) return;
    const t = e.touches[0];
    const ddx = t.clientX - start.current.x;
    const ddy = t.clientY - start.current.y;

    if (!axis.current) {
      if (Math.abs(ddx) < 8 && Math.abs(ddy) < 8) return;
      axis.current = Math.abs(ddx) > Math.abs(ddy) ? 'h' : 'v';
    }
    if (axis.current !== 'h') return;

    // Right only — a left swipe has no action, so it should not move at all.
    setDx(Math.max(0, Math.min(ddx, SWIPE_MAX)));
  };

  const onTouchEnd = () => {
    const shouldCall = axis.current === 'h' && dx >= SWIPE_TRIGGER && !!number;
    setAnimating(true);
    setDx(0);
    start.current = null;
    axis.current = null;
    if (shouldCall) window.location.href = `tel:${number}`;
  };

  return (
    <div className="relative overflow-hidden">
      {/* Revealed behind the row as it slides */}
      <div
        className={`absolute inset-y-0 left-0 flex items-center pl-5 transition-colors ${
          armed ? 'bg-emerald-600' : 'bg-emerald-500'
        }`}
        style={{ width: Math.max(dx, 0), opacity: dx > 0 ? 1 : 0 }}
        aria-hidden
      >
        <Phone className="h-5 w-5 text-white shrink-0" />
      </div>

      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{
          transform: `translateX(${dx}px)`,
          transition: animating ? 'transform 180ms ease-out' : 'none',
          touchAction: 'pan-y',
        }}
        className={`relative bg-white ${className || ''}`}
      >
        {children}
      </div>
    </div>
  );
};

/**
 * Module scope, not nested inside CallerIdPanel: a component declared inside
 * another is a new type on every render, so React remounts the whole subtree
 * each time. Harmless for these static rows, but the same mistake in a picker
 * with a text input silently eats keystrokes.
 */
const ChecklistRow: React.FC<{
  label: string; done: boolean; hint?: string; action?: React.ReactNode;
}> = ({ label, done, hint, action }) => (
  <div className="flex items-center gap-2 py-2 border-b border-gray-50 last:border-0">
    <span className={`h-4 w-4 shrink-0 rounded-full flex items-center justify-center text-[10px] ${
      done ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'
    }`}>
      {done ? '✓' : '!'}
    </span>
    <div className="flex-1 min-w-0">
      <div className="text-xs text-gray-700">{label}</div>
      {hint && <div className="text-[11px] text-gray-400">{hint}</div>}
    </div>
    {action}
  </div>
);

/**
 * Setup for the Truecaller-style popup. Three things must line up before it can
 * work — phone-state permission, the overlay permission, and a populated cache —
 * so each is shown as its own row with its own action rather than hidden behind
 * one switch that silently does nothing.
 */
const CallerIdPanel: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<CallerIdStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setStatus(await getCallerIdStatus());
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  // The overlay permission is granted on a Settings screen with no callback, so
  // the only reliable signal that it changed is the user coming back to the app.
  useEffect(() => {
    if (!open) return;
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [open, refresh]);

  const act = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
    } catch (e: any) {
      showError('Caller ID', e?.message || 'Something went wrong');
    } finally {
      setBusy(null);
      refresh();
    }
  };

  // Permissions are the only hard prerequisite — an empty cache is something
  // the Turn on button fixes for itself.
  const permsReady = !!status?.phonePermission && !!status?.overlayPermission;
  const ready = permsReady && (status?.cachedContacts ?? 0) > 0;

  const btn ="px-2.5 py-1 rounded-md border border-gray-200 text-[11px] text-blue-600 disabled:opacity-50 shrink-0";

  return (
    <div className="mt-4 mx-3 border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 text-xs font-medium text-gray-600"
      >
        <span className="flex items-center gap-1.5">
          <BellRing className="h-3.5 w-3.5" />
          Caller ID popup
          {status && (
            <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${
              status.enabled && ready ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'
            }`}>
              {status.enabled && ready ? 'On' : 'Off'}
            </span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="p-3">
          <p className="text-[11px] text-gray-500 mb-2">
            Shows the customer name over the dialer when a known number rings, and a
            summary when the call ends. Matching happens on the phone from a cached
            copy of your contacts, so it works instantly and with no signal.
          </p>

          <ChecklistRow
            label="Phone state access"
            done={!!status?.phonePermission}
            hint="Lets the app know when a call arrives"
            action={!status?.phonePermission ? (
              <button className={btn} disabled={busy === 'phone'}
                onClick={() => act('phone', async () => { await requestPhonePermission(); })}>
                Allow
              </button>
            ) : undefined}
          />

          <ChecklistRow
            label="Draw over other apps"
            done={!!status?.overlayPermission}
            hint="Opens a Settings screen — come back when done"
            action={!status?.overlayPermission ? (
              <button className={btn} disabled={busy === 'overlay'}
                onClick={() => act('overlay', async () => { await requestOverlayPermission(); })}>
                Open
              </button>
            ) : undefined}
          />

          {/* The one that decides WHEN the card appears. Android stopped giving
              ordinary apps the caller's number while ringing, so without this
              role the card can only be shown after the call ends. */}
          <ChecklistRow
            label="Caller ID app role"
            done={!!status?.screeningRole}
            hint={status?.screeningRole
              ? 'Name shows while the phone is ringing'
              : 'Without this the card only appears after the call ends'}
            action={!status?.screeningRole ? (
              <button className={btn} disabled={busy === 'role'}
                onClick={() => act('role', async () => {
                  const ok = await requestScreeningRole();
                  if (ok) showSuccess('Caller ID app set', 'Names will now show while the phone rings.');
                })}>
                Set
              </button>
            ) : undefined}
          />

          <ChecklistRow
            label={`Contacts cached${status?.cachedContacts ? `: ${status.cachedContacts.toLocaleString()}` : ''}`}
            done={(status?.cachedContacts ?? 0) > 0}
            hint="Re-sync after adding contacts"
            action={(
              <button className={btn} disabled={busy === 'sync'}
                onClick={() => act('sync', async () => {
                  const n = await syncDirectory();
                  showSuccess('Directory synced', `${n.toLocaleString()} contacts stored on this phone.`);
                })}>
                {busy === 'sync' ? 'Syncing...' : 'Sync now'}
              </button>
            )}
          />

          <div className="flex gap-2 mt-3">
            {/* One button, not two. Requiring "Sync now" before "Turn on" made
                it far too easy to enable nothing, or to leave the cache empty
                and have every caller show as Unknown. Turning it on now pulls
                the directory first if the cache is empty. */}
            <button
              disabled={!permsReady || busy === 'toggle'}
              onClick={() => act('toggle', async () => {
                if (status?.enabled) {
                  await setCallerIdEnabled(false);
                  showSuccess('Caller ID off', 'Popups are disabled.');
                  return;
                }
                if ((status?.cachedContacts ?? 0) === 0) {
                  const n = await syncDirectory();
                  showSuccess('Contacts synced', `${n.toLocaleString()} contacts cached on this phone.`);
                }
                await setCallerIdEnabled(true);
                showSuccess('Caller ID on', 'You will see who is calling before you answer.');
              })}
              className={`flex-1 py-2 rounded-md text-xs font-medium disabled:opacity-50 ${
                status?.enabled ? 'bg-gray-800 text-white' : 'bg-blue-600 text-white'
              }`}
            >
              {busy === 'toggle'
                ? 'Working...'
                : status?.enabled ? 'Turn off' : 'Turn on caller ID'}
            </button>
            <button
              disabled={!status?.overlayPermission}
              onClick={() => act('test', async () => { await showTestPopup(); })}
              className="flex-1 py-2 rounded-md border border-gray-200 text-xs text-gray-600 disabled:opacity-50"
            >
              Test popup
            </button>
          </div>

          {!permsReady && (
            <p className="text-[11px] text-amber-600 mt-2">
              Grant both permissions above before turning it on.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * On-screen replacement for logcat. Several vendor ROMs (vivo/Funtouch among
 * them) refuse to open Developer options at all, which rules out adb, so this
 * reports everything needed to tell an empty screen apart from a stale APK, a
 * denied permission, or a lookup that matched nothing.
 */
const DiagnosticsPanel: React.FC<{ matched?: number; total?: number }> = ({ matched, total }) => {
  const { showSuccess, showError } = useToast();
  const [open, setOpen] = useState(false);
  const [diag, setDiag] = useState<CallLogDiagnostics | null>(null);
  const [running, setRunning] = useState(false);

  const run = useCallback(async () => {
    setRunning(true);
    try {
      setDiag(await probeCallLog());
    } finally {
      setRunning(false);
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !diag) run();
  };

  const rows: { label: string; value: string; bad?: boolean }[] = diag
    ? [
        { label: 'Running in Android app', value: diag.isNative ? 'Yes' : 'No', bad: !diag.isNative },
        { label: 'Plugin in installed APK', value: diag.pluginInstalled ? 'Yes' : 'No — rebuild & reinstall the APK', bad: !diag.pluginInstalled },
        { label: 'Call log permission', value: diag.permission, bad: diag.permission !== 'granted' },
        { label: 'Calls read from phone', value: diag.rawCallCount === null ? 'not reached' : String(diag.rawCallCount), bad: diag.rawCallCount === 0 },
        { label: 'Matched to customers', value: typeof matched === 'number' ? `${matched} of ${total ?? 0}` : 'n/a' },
        { label: 'Platform', value: diag.platform },
        { label: 'Last native error', value: diag.lastError || 'none', bad: !!diag.lastError },
      ]
    : [];

  const copy = async () => {
    const text = rows.map(r => `${r.label}: ${r.value}`).join('\n') + `\n\nUA: ${diag?.userAgent || ''}`;
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('Copied', 'Diagnostics copied to the clipboard.');
    } catch {
      showError('Could not copy', 'Clipboard is blocked — read the values off the screen instead.');
    }
  };

  return (
    <div className="mt-6 mx-3 border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 text-xs font-medium text-gray-600"
      >
        <span className="flex items-center gap-1.5">
          <Stethoscope className="h-3.5 w-3.5" />
          Diagnostics
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="p-3 text-xs">
          {running && <div className="text-gray-400">Checking...</div>}

          {!running && diag && (
            <>
              <table className="w-full">
                <tbody>
                  {rows.map(r => (
                    <tr key={r.label} className="border-b border-gray-50 last:border-0">
                      <td className="py-1.5 pr-2 text-gray-500 align-top">{r.label}</td>
                      <td className={`py-1.5 text-right font-medium break-all ${r.bad ? 'text-red-600' : 'text-gray-800'}`}>
                        {r.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex gap-2 mt-3">
                <button onClick={run} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md border border-gray-200 text-gray-600">
                  <RefreshCw className="h-3.5 w-3.5" />
                  Re-run
                </button>
                <button onClick={copy} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md border border-gray-200 text-gray-600">
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CallLog: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [permission, setPermission] = useState<'unknown' | 'granted' | 'denied'>('unknown');
  const [calls, setCalls] = useState<DeviceCall[]>([]);
  const [lookup, setLookup] = useState<Record<string, ContactLookupHit>>({});
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [offline, setOffline] = useState(false);
  // Arriving from the Call Summary report as /call-log?day=2026-07-31 (and
  // optionally &type=noanswer) means "show me those calls" — which only works if
  // the whole log is read, since the day is usually older than the default window.
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const openDay = params.get('day');
  const [filter, setFilter] = useState<Filter>(
    FILTERS.some(f => f.key === params.get('type')) ? (params.get('type') as Filter) : 'all',
  );
  const [range, setRange] = useState<Range>(openDay ? 'all' : '7d');
  const [truncated, setTruncated] = useState(false);
  const [renderLimit, setRenderLimit] = useState(RENDER_PAGE);
  const [merged, setMerged] = useState(false);
  const [query, setQuery] = useState('');
  const [linkTarget, setLinkTarget] = useState<DeviceCall | null>(null);
  // Day-wise totals. Remembered, because someone who wants the numbers today
  // usually wants them tomorrow as well.
  const [showSummary, setShowSummary] = useState<boolean>(() => {
    // Opened on a specific day, the panel is also the way back out of it.
    if (openDay) return true;
    try { return localStorage.getItem('call-log-summary') === '1'; } catch { return false; }
  });
  // Tapping a day in the summary pins the list to that day.
  const [dayPick, setDayPick] = useState<string | null>(openDay);

  // Resolve the distinct numbers on screen. A wide date range can hold more
  // unique numbers than the endpoint accepts at once, so this goes out in
  // chunks and merges — one request per 400 numbers rather than one per row.
  const resolveNumbers = useCallback(async (rows: DeviceCall[]) => {
    // Plain-object dedupe rather than a Set — the app builds to ES5.
    const seen: Record<string, boolean> = {};
    const numbers: string[] = [];
    rows.forEach(c => {
      const n = normalizeNumber(c.number);
      if (n && !seen[n]) {
        seen[n] = true;
        numbers.push(n);
      }
    });
    if (numbers.length === 0) {
      setLookup({});
      return;
    }
    setResolving(true);
    try {
      const CHUNK = 400;
      const merged: Record<string, ContactLookupHit> = {};
      for (let i = 0; i < numbers.length; i += CHUNK) {
        const res = await customersApi.lookupContacts(numbers.slice(i, i + CHUNK));
        Object.assign(merged, res.data || {});
      }
      setLookup(merged);
      setOffline(false);
    } catch (e: any) {
      if (isOffline(e)) {
        setOffline(true);
        // Not an error the user caused or can fix by retrying the lookup — the
        // calls below are read from the handset and remain perfectly usable.
        showError('No connection to server', 'Caller names are unavailable until the connection is back. Your call list still works.');
      } else {
        showError('Lookup failed', e?.message || 'Could not match numbers against the customer database');
      }
    } finally {
      setResolving(false);
    }
  }, [showError]);

  const load = useCallback(async (forRange: Range) => {
    const cfg = RANGES.find(r => r.key === forRange) || RANGES[0];
    setLoading(true);
    try {
      const rows = await getDeviceCallLog(cfg.limit, rangeSince(forRange));
      setCalls(rows);
      setTruncated(rows.length >= cfg.limit);
      await resolveNumbers(rows);
    } catch (e: any) {
      showError('Could not read call log', e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [resolveNumbers, showError]);

  useEffect(() => {
    if (!isCallLogAvailable) return;
    checkCallLogPermission().then(granted => {
      setPermission(granted ? 'granted' : 'denied');
      if (granted) load(range);
    });
    // Deliberately mount-only: re-running whenever `load` changes identity would
    // re-read the whole call log on every render. Range changes are handled by
    // pickRange, which reads from the device again with the new bound.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickRange = (next: Range) => {
    setRange(next);
    // A day pinned from the old range may not exist in the new one, and an
    // empty list with no visible reason is worse than losing the pin.
    setDayPick(null);
    if (permission === 'granted') load(next);
  };

  const askPermission = async () => {
    const granted = await requestCallLogPermission();
    setPermission(granted ? 'granted' : 'denied');
    if (granted) {
      load(range);
    } else {
      showError(
        'Permission denied',
        'Call log access is needed to show who called. Enable it in Settings > Apps > ABS Cloud > Permissions.'
      );
    }
  };

  const hitFor = useCallback(
    (c: DeviceCall) => lookup[normalizeNumber(c.number)],
    [lookup]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return calls.filter(c => {
      const hit = hitFor(c);
      if (dayPick && dayKey(c.date) !== dayPick) return false;
      if (filter === 'missed' && c.type !== CALL_TYPE.MISSED) return false;
      if (filter === 'incoming' && c.type !== CALL_TYPE.INCOMING) return false;
      if (filter === 'outgoing' && c.type !== CALL_TYPE.OUTGOING) return false;
      // Dialled but never answered: the handset records it as an outgoing call
      // that lasted no time at all.
      if (filter === 'noanswer' && !(c.type === CALL_TYPE.OUTGOING && !c.duration)) return false;
      if (filter === 'unknown' && hit) return false;
      if (!q) return true;
      return (
        (c.number || '').toLowerCase().includes(q) ||
        (c.cachedName || '').toLowerCase().includes(q) ||
        (hit?.contact_person || '').toLowerCase().includes(q) ||
        (hit?.company || '').toLowerCase().includes(q)
      );
    });
  }, [calls, filter, query, hitFor, dayPick]);

  // Only this slice is mounted; "Show more" extends it. Reset whenever the set
  // of matching calls changes, so a new filter always starts from the top.
  const shown = useMemo(() => visible.slice(0, renderLimit), [visible, renderLimit]);

  // Merged view: one entry per number, holding every call with it. `visible` is
  // already newest-first, so calls[0] is that number's most recent call and the
  // entries come out ordered by most-recently-contacted without another sort.
  const mergedAll = useMemo(() => {
    const map: Record<string, { key: string; number: string; calls: DeviceCall[] }> = {};
    const order: string[] = [];
    for (const c of visible) {
      // Withheld numbers have nothing to merge on, so they stay separate rows.
      const key = normalizeNumber(c.number) || `private-${c.date}`;
      if (!map[key]) {
        map[key] = { key, number: c.number || '', calls: [] };
        order.push(key);
      }
      map[key].calls.push(c);
    }
    return order.map(k => map[k]);
  }, [visible]);

  const mergedShown = useMemo(
    () => mergedAll.slice(0, renderLimit),
    [mergedAll, renderLimit]
  );

  // Day headings for the merged view, keyed on each number's most recent call.
  // Without them the merged list is an undated run of names with no way to see
  // what came in today. Entries are already ordered most-recently-contacted
  // first, so consecutive grouping is enough — no second sort.
  const mergedGrouped = useMemo(() => {
    const out: { label: string; rows: typeof mergedShown }[] = [];
    for (const entry of mergedShown) {
      const label = dayLabel(entry.calls[0].date);
      const last = out[out.length - 1];
      if (last && last.label === label) last.rows.push(entry);
      else out.push({ label, rows: [entry] });
    }
    return out;
  }, [mergedShown]);

  const listTotal = merged ? mergedAll.length : visible.length;
  const listShown = merged ? mergedShown.length : shown.length;

  useEffect(() => {
    setRenderLimit(RENDER_PAGE);
  }, [filter, range, query, calls, merged, dayPick]);

  // Preserve the newest-first ordering the provider returned; just bucket by day.
  const grouped = useMemo(() => {
    const out: { label: string; rows: DeviceCall[] }[] = [];
    for (const c of shown) {
      const label = dayLabel(c.date);
      const last = out[out.length - 1];
      if (last && last.label === label) last.rows.push(c);
      else out.push({ label, rows: [c] });
    }
    return out;
  }, [shown]);

  // One row per day: how many came in, went out, rang out unanswered, were
  // missed. Counted over every call loaded for the chosen range and narrowed by
  // the search box, but NOT by the type filter — comparing the types is the
  // whole point.
  const daySummary = useMemo(() => {
    type Row = { key: string; label: string; incoming: number; outgoing: number; noAnswer: number; missed: number; other: number; total: number };
    const q = query.trim().toLowerCase();
    const rows: Row[] = [];
    const byKey: Record<string, Row> = {};
    for (const c of calls) {
      if (q) {
        const hit = hitFor(c);
        const hay = [c.number, c.cachedName, hit?.contact_person, hit?.company].join(' ').toLowerCase();
        if (!hay.includes(q)) continue;
      }
      const key = dayKey(c.date);
      if (!byKey[key]) {
        byKey[key] = { key, label: dayLabel(c.date), incoming: 0, outgoing: 0, noAnswer: 0, missed: 0, other: 0, total: 0 };
        rows.push(byKey[key]);
      }
      const row = byKey[key];
      row.total++;
      if (c.type === CALL_TYPE.INCOMING) row.incoming++;
      else if (c.type === CALL_TYPE.OUTGOING) {
        if (c.duration > 0) row.outgoing++;
        else row.noAnswer++;
      }
      else if (c.type === CALL_TYPE.MISSED) row.missed++;
      else row.other++;
    }
    // The device returns calls newest-first, so the days are already in order.
    return rows;
  }, [calls, query, hitFor]);

  const summaryTotals = useMemo(
    () => daySummary.reduce(
      (t, d) => ({
        incoming: t.incoming + d.incoming,
        outgoing: t.outgoing + d.outgoing,
        noAnswer: t.noAnswer + d.noAnswer,
        missed: t.missed + d.missed,
        total: t.total + d.total,
      }),
      { incoming: 0, outgoing: 0, noAnswer: 0, missed: 0, total: 0 },
    ),
    [daySummary],
  );

  const unknownCount = useMemo(
    () => calls.filter(c => c.number && !hitFor(c)).length,
    [calls, hitFor]
  );

  const matchedCount = useMemo(
    () => calls.filter(c => hitFor(c)).length,
    [calls, hitFor]
  );

  if (!isCallLogAvailable) {
    return (
      <div className="py-6 max-w-md mx-auto">
        <div className="px-6 text-center">
          <Smartphone className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h2 className="text-base font-semibold text-gray-800">Call Log is mobile only</h2>
          <p className="text-sm text-gray-500 mt-1">
            This screen reads the call history stored on the handset, so it works only in the
            ABS Cloud Android app and not in a desktop browser.
          </p>
        </div>
        <DiagnosticsPanel />
      </div>
    );
  }

  if (permission !== 'granted') {
    return (
      <div className="py-6 max-w-md mx-auto">
        <div className="px-6 text-center">
          <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-base font-semibold text-gray-800">Allow call log access</h2>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            ABS Cloud reads your call history on this device only, to show which customer each
            number belongs to. Nothing is uploaded to the server.
          </p>
          <button
            onClick={askPermission}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium"
          >
            Allow access
          </button>
        </div>
        <DiagnosticsPanel />
      </div>
    );
  }

  return (
    <div className="pb-20">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search name, company or number"
              className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-400"
            />
          </div>
          <button
            onClick={() => setShowSummary(v => {
              const next = !v;
              try { localStorage.setItem('call-log-summary', next ? '1' : '0'); } catch { /* private mode */ }
              return next;
            })}
            className={`p-2 rounded-lg border ${
              showSummary ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-200 text-gray-500'
            }`}
            aria-label={showSummary ? 'Hide the day-wise summary' : 'Show the day-wise summary'}
            title="Day-wise totals"
          >
            <CalendarDays className="h-4 w-4" />
          </button>
          <button
            onClick={() => setMerged(m => !m)}
            className={`p-2 rounded-lg border ${
              merged ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 text-gray-500'
            }`}
            aria-label={merged ? 'Show every call separately' : 'Merge calls by number'}
            title={merged ? 'Showing one row per number' : 'Merge by number'}
          >
            <Layers className="h-4 w-4" />
          </button>
          <button
            onClick={() => load(range)}
            disabled={loading}
            className="p-2 rounded-lg border border-gray-200 text-gray-500 disabled:opacity-50"
            aria-label="Refresh call log"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex gap-1.5 px-3 pb-2 overflow-x-auto">
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => pickRange(r.key)}
              disabled={loading}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap border disabled:opacity-50 ${
                range === r.key
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="flex gap-1.5 px-3 pb-2 overflow-x-auto">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap border ${
                filter === f.key
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              {f.label}
              {f.key === 'unknown' && unknownCount > 0 && ` (${unknownCount})`}
            </button>
          ))}
        </div>
      </div>

      {/* Day-wise totals: how many calls came in, went out and were missed on
          each day of the chosen range. Tapping a day pins the list below to it. */}
      {showSummary && (
        <div className="mx-3 mt-2 rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
            <CalendarDays className="h-4 w-4 text-indigo-600" />
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Day-wise summary</span>
            <span className="text-[11px] text-gray-400">{daySummary.length} day(s)</span>
            {dayPick && (
              <button
                onClick={() => setDayPick(null)}
                className="ml-auto text-[11px] font-semibold text-blue-600"
              >
                Clear day filter
              </button>
            )}
          </div>

          {daySummary.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-gray-400">No calls in this range.</div>
          ) : (
            <>
              <div className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold uppercase text-gray-400 border-b border-gray-100">
                <span className="flex-1">Day</span>
                <span className="w-10 text-right">In</span>
                <span className="w-10 text-right">Out</span>
                <span className="w-12 text-right text-amber-600">No ans</span>
                <span className="w-10 text-right">Miss</span>
                <span className="w-10 text-right">Total</span>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                {daySummary.map(d => (
                  <button
                    key={d.key}
                    onClick={() => setDayPick(p => (p === d.key ? null : d.key))}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                      dayPick === d.key ? 'bg-blue-50' : 'bg-white'
                    }`}
                  >
                    <span className="flex-1 font-medium text-gray-800 truncate">{d.label}</span>
                    <span className="w-10 text-right tabular-nums text-emerald-600 font-semibold">{d.incoming || '—'}</span>
                    <span className="w-10 text-right tabular-nums text-blue-600 font-semibold">{d.outgoing || '—'}</span>
                    <span className="w-12 text-right tabular-nums text-amber-700 font-semibold">{d.noAnswer || '—'}</span>
                    <span className="w-10 text-right tabular-nums text-red-500 font-semibold">{d.missed || '—'}</span>
                    <span className="w-10 text-right tabular-nums text-gray-700 font-bold">{d.total}</span>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-50 border-t border-gray-200">
                <span className="flex-1 font-bold text-gray-700">Total</span>
                <span className="w-10 text-right tabular-nums text-emerald-700 font-bold">{summaryTotals.incoming}</span>
                <span className="w-10 text-right tabular-nums text-blue-700 font-bold">{summaryTotals.outgoing}</span>
                <span className="w-12 text-right tabular-nums text-amber-700 font-bold">{summaryTotals.noAnswer}</span>
                <span className="w-10 text-right tabular-nums text-red-600 font-bold">{summaryTotals.missed}</span>
                <span className="w-10 text-right tabular-nums text-gray-900 font-bold">{summaryTotals.total}</span>
              </div>
              {/* Out and No ans are both calls this phone placed, so say which
                  is which instead of leaving two similar columns to be guessed. */}
              <div className="px-3 py-2 text-[11px] text-gray-500 border-t border-gray-100 leading-relaxed">
                <strong>Out</strong> was answered; <strong className="text-amber-700">No ans</strong> was
                dialled but never picked up. The <strong>Not received</strong> filter above lists them.
              </div>
            </>
          )}
        </div>
      )}

      {/* A toast disappears; a dropped connection does not. This stays put so
          "no names shown" is never mistaken for "these numbers are unknown". */}
      {offline && !resolving && (
        <div className="flex items-center gap-2 mx-3 mt-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
          <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="flex-1 text-xs text-amber-800">
            No connection to the server — caller names unavailable.
          </span>
          <button
            onClick={() => resolveNumbers(calls)}
            className="text-xs font-medium text-amber-700 shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {resolving && (
        <div className="px-3 py-1.5 text-xs text-gray-400">Matching numbers...</div>
      )}

      {!loading && !resolving && calls.length > 0 && (
        <div className="px-3 py-1.5 text-xs text-gray-400">
          {merged
            ? `${mergedAll.length} ${mergedAll.length === 1 ? 'number' : 'numbers'} · ${visible.length} calls`
            : visible.length === calls.length
              ? `${calls.length} calls`
              : `${visible.length} of ${calls.length} calls`}
          {matchedCount > 0 && ` · ${matchedCount} matched`}
          {truncated && ' · device holds more than this range can read'}
        </div>
      )}

      {!loading && visible.length === 0 && (
        <div className="p-8 text-center text-sm text-gray-400">
          {calls.length === 0
            ? `No calls in this period.`
            : 'No calls match this filter.'}
        </div>
      )}

      {merged && mergedGrouped.map(group => (
        <div key={group.label}>
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 bg-gray-50">
            {group.label}
          </div>
          {group.rows.map(entry => {
        const last = entry.calls[0];
        const hit = hitFor(last);
        const name = hit?.contact_person || last.cachedName || null;
        const digits = normalizeNumber(last.number);
        const missedCount = entry.calls.filter(c => c.type === CALL_TYPE.MISSED).length;
        // The actionable signal is the MOST RECENT call being missed — that is
        // the number still owed a call back. A number with old missed calls that
        // was since spoken to is already dealt with, so it stays neutral.
        const lastMissed = last.type === CALL_TYPE.MISSED;

        return (
          <SwipeToCall
            key={entry.key}
            number={last.number}
            className={`border-b border-gray-50 ${
              lastMissed ? '!bg-red-50 border-l-[3px] border-l-red-500' : ''
            }`}
          >
            <div className="flex items-start gap-3 px-3 py-2.5">
              <div className="pt-1">{callIcon(last.type)}</div>

              <button
                onClick={() => navigate(`/call-log/${entry.key}`, { state: { calls: entry.calls } })}
                className="flex-1 min-w-0 text-left"
              >
                <div className="flex items-center gap-1.5">
                  <span className={`text-sm font-medium truncate ${!name ? 'italic ' : ''}${
                    lastMissed ? 'text-red-700' : name ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {name || 'Unknown'}
                  </span>
                  {hit?.primary_contact && (
                    <Star className="h-3 w-3 text-amber-400 fill-amber-400 shrink-0" />
                  )}
                </div>

                {hit && (
                  <div className="flex items-center gap-1 text-xs text-blue-600 max-w-full">
                    <Building2 className="h-3 w-3 shrink-0" />
                    <span className="truncate">{hit.company}</span>
                  </div>
                )}

                <div className="text-xs text-gray-400 truncate">
                  {displayNumber(last.number) || 'Private number'} &middot; {dayLabel(last.date)} {formatTime(last.date)}
                </div>

                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                    {entry.calls.length} {entry.calls.length === 1 ? 'call' : 'calls'}
                  </span>
                  {/* Solid when the latest call is the missed one — that is a
                      callback still owed. An older missed call stays muted. */}
                  {lastMissed ? (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-600 text-white font-medium">
                      Last call missed{missedCount > 1 ? ` (${missedCount})` : ''}
                    </span>
                  ) : missedCount > 0 ? (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-600">
                      {missedCount} missed
                    </span>
                  ) : null}
                </div>
              </button>

              {/* Call, WhatsApp and the company shortcut all live on the detail
                  page now — swipe right to call, tap to open. The only action
                  kept here is adding an unknown number, because working through
                  the Unknown filter is the one flow that would otherwise need a
                  round trip per number. */}
              {!hit && last.number && (
                <button
                  onClick={() => setLinkTarget(last)}
                  className="p-2 rounded-lg bg-blue-50 text-blue-600 shrink-0"
                  aria-label="Add to a customer"
                >
                  <UserPlus className="h-4 w-4" />
                </button>
              )}
            </div>
          </SwipeToCall>
        );
          })}
        </div>
      ))}

      {!merged && grouped.map(group => (
        <div key={group.label}>
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 bg-gray-50">
            {group.label}
          </div>
          {group.rows.map((c, i) => {
            const hit = hitFor(c);
            const name = hit?.contact_person || c.cachedName || null;
            const digits = normalizeNumber(c.number);
            const isMissed = c.type === CALL_TYPE.MISSED;
            return (
              <SwipeToCall
                key={`${c.date}-${i}`}
                number={c.number}
                className={`flex items-start gap-3 px-3 py-2.5 border-b border-gray-50 ${
                  isMissed ? '!bg-red-50 border-l-[3px] border-l-red-500' : ''
                }`}
              >
                <div className="pt-1">{callIcon(c.type)}</div>

                <button
                  onClick={() => navigate(`/call-log/${digits || c.number}`)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`text-sm font-medium truncate ${!name ? 'italic ' : ''}${
                        isMissed ? 'text-red-700' : name ? 'text-gray-900' : 'text-gray-400'
                      }`}
                    >
                      {name || 'Unknown'}
                    </span>
                    {hit?.primary_contact && (
                      <Star className="h-3 w-3 text-amber-400 fill-amber-400 shrink-0" />
                    )}
                  </div>

                  {/* Plain text, not a link: the whole row is now a button to
                      the number's detail page, where each mapped company gets
                      its own button. A nested button would be invalid HTML. */}
                  {hit && (
                    <span className="flex items-center gap-1 text-xs text-blue-600 max-w-full">
                      <Building2 className="h-3 w-3 shrink-0" />
                      <span className="truncate">{hit.company}</span>
                    </span>
                  )}
                  {hit && hit.others?.length > 0 && (
                    <span className="block text-[11px] text-gray-400 truncate">
                      +{hit.others.length} more {hit.others.length === 1 ? 'company' : 'companies'}
                    </span>
                  )}

                  <span className="block text-xs text-gray-400 truncate">
                    {displayNumber(c.number) || 'Private number'} &middot; {formatTime(c.date)}
                    {formatDuration(c.duration) && ` · ${formatDuration(c.duration)}`}
                  </span>
                </button>

                {!hit && c.number && (
                  <button
                    onClick={() => setLinkTarget(c)}
                    className="p-2 rounded-lg bg-blue-50 text-blue-600 shrink-0"
                    aria-label="Add to a customer"
                  >
                    <UserPlus className="h-4 w-4" />
                  </button>
                )}
              </SwipeToCall>
            );
          })}
        </div>
      ))}

      {listShown < listTotal && (
        <button
          onClick={() => setRenderLimit(n => n + RENDER_PAGE)}
          className="w-full py-3 text-sm font-medium text-blue-600"
        >
          Show more ({listTotal - listShown} remaining)
        </button>
      )}

      <CallerIdPanel />

      <DiagnosticsPanel matched={matchedCount} total={calls.length} />

      {linkTarget && (
        <LinkContactSheet
          call={linkTarget}
          onClose={() => setLinkTarget(null)}
          onSaved={async () => {
            setLinkTarget(null);
            showSuccess('Contact saved', 'The number is now mapped to the customer.');
            await resolveNumbers(calls);
          }}
        />
      )}
    </div>
  );
};

/** Bottom sheet: pick a customer, name the person, save into customer_contact_details. */
const LinkContactSheet: React.FC<{
  call: DeviceCall;
  onClose: () => void;
  onSaved: () => void;
}> = ({ call, onClose, onSaved }) => {
  const { showError } = useToast();
  const [custQuery, setCustQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const [person, setPerson] = useState(call.cachedName || '');
  const [primary, setPrimary] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = custQuery.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await customersApi.search(q);
        if (!cancelled) setResults(res.data || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [custQuery]);

  const save = async () => {
    if (!selected) {
      showError('Pick a customer', 'Choose which customer this number belongs to.');
      return;
    }
    if (!person.trim()) {
      showError('Name required', 'Enter the contact person name.');
      return;
    }
    setSaving(true);
    try {
      await customersApi.createContact(selected.id, {
        contact_person: person.trim(),
        mobile_no: (call.number || '').replace(/\s/g, ''),
        primary_contact: primary ? 'Yes' : 'No',
      });
      onSaved();
    } catch (e: any) {
      showError('Could not save contact', e?.message || 'Unknown error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end bg-black/40" onClick={onClose}>
      {/* Flex column with a pinned footer — with the customer picker open and the
          keyboard up, a Save button at the end of the content scrolls out of reach. */}
      <div
        className="w-full bg-white rounded-t-2xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0 bg-white">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Add contact</h3>
            <p className="text-xs text-gray-400">{displayNumber(call.number)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Customer</label>
            {selected ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
                <span className="text-sm text-blue-900 truncate">{selected.company}</span>
                <button
                  onClick={() => {
                    setSelected(null);
                    setCustQuery('');
                  }}
                  className="text-xs text-blue-600 shrink-0"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <input
                  value={custQuery}
                  onChange={e => setCustQuery(e.target.value)}
                  placeholder="Type at least 2 letters of the company name"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-blue-400"
                  autoFocus
                />
                {searching && <div className="text-xs text-gray-400 mt-1">Searching...</div>}
                {results.length > 0 && (
                  <div className="mt-1 border border-gray-100 rounded-lg divide-y divide-gray-50 max-h-56 overflow-y-auto">
                    {results.map(r => (
                      <button
                        key={r.id}
                        onClick={() => setSelected(r)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        {r.company}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Contact person</label>
            <input
              value={person}
              onChange={e => setPerson(e.target.value)}
              placeholder="Person name"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-blue-400"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={primary}
              onChange={e => setPrimary(e.target.checked)}
              className="h-4 w-4"
            />
            Set as primary contact for this customer
          </label>
        </div>

        <div className="shrink-0 p-3 border-t border-gray-100 bg-white">
          <button
            onClick={save}
            disabled={saving || !selected || !person.trim()}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save contact'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallLog;
